var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React, { useMemo } from 'react';
import { Platform, Text, View } from 'react-native';
import Toast from 'react-native-root-toast';
import styled from 'styled-components/native';
import PlusIcon from '@/assets/icon/ic_plus.svg';
import { SccTouchableOpacity } from '@/components/SccTouchableOpacity';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { RecommendedMobilityTypeDto, SpaciousTypeDto, } from '@/generated-sources/openapi';
import useNavigateWithLocationCheck from '@/hooks/useNavigateWithLocationCheck';
import useNavigation from '@/navigation/useNavigation';
import { useCheckAuth } from '@/utils/checkAuth';
import * as SS from '../sections/PlaceDetailEntranceSection.style';
export default function PlaceReviewSummaryInfo({ reviews, placeId, placeName, placeLocation, placeAddress, hideWriteButton, }) {
    const navigation = useNavigation();
    const checkAuth = useCheckAuth();
    const { navigateWithLocationCheck, LocationConfirmModal } = useNavigateWithLocationCheck();
    const handleReviewPress = () => {
        if (Platform.OS === 'web') {
            Toast.show('준비 중입니다 💪', {
                duration: Toast.durations.SHORT,
                position: Toast.positions.BOTTOM,
            });
            return;
        }
        checkAuth(() => __awaiter(this, void 0, void 0, function* () {
            yield navigateWithLocationCheck({
                targetLocation: placeLocation,
                placeName: placeName,
                address: placeAddress,
                type: 'place',
                onNavigate: () => {
                    navigation.navigate('ReviewForm/Place', {
                        placeId,
                    });
                },
            });
        }));
    };
    const mobilityTypeCounts = useMemo(() => countMobilityTypes(reviews), [reviews]);
    const spaciousTypeCounts = useMemo(() => countSpaciousType(reviews), [reviews]);
    if (reviews.length === 0) {
        return null;
    }
    const spaciousTypeMax = Math.max(...spaciousTypeCounts.map(s => s.count)) || 1;
    return (_jsxs(Container, { children: [_jsxs(HeaderRow, { children: [_jsxs(HeaderLeft, { children: [_jsx(SS.Title, { children: "\uBC29\uBB38 \uB9AC\uBDF0" }), _jsx(ReviewCount, { children: reviews.length })] }), !hideWriteButton && (_jsxs(ReviewButton, { elementName: "place_detail_review_write_button", onPress: handleReviewPress, children: [_jsx(PlusIcon, { color: color.white }), _jsx(ReviewButtonText, { children: "\uB9AC\uBDF0 \uC4F0\uAE30" })] }))] }), _jsx(SectionColumn, { style: { marginTop: 16 }, children: _jsx(TextBoxThinRow, { children: spaciousTypeCounts.map(item => (_jsx(SpaciousTextBox, { label: item.label, content: `${item.count}명`, level: item.level, filledRatio: item.count / spaciousTypeMax }, item.label))) }) }), _jsxs(MobilitySectionColumn, { children: [_jsx(SectionTitle, { children: "\uCD94\uCC9C\uB300\uC0C1" }), _jsxs(MobilityCardGrid, { children: [_jsx(TextBoxRow, { children: mobilityTypeCounts.slice(0, 3).map(item => (_jsx(TextBox, { label: item.label, content: `${item.count}명`, level: item.level, shape: "normal" }, item.label))) }), _jsx(TextBoxRow, { children: mobilityTypeCounts.slice(3, 5).map(item => (_jsx(TextBox, { label: item.label.replace('\n', ' '), content: `${item.count}명`, level: item.level, shape: "flat" }, item.label))) })] })] }), LocationConfirmModal] }));
}
const Container = styled.View ``;
const HeaderRow = styled.View `
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;
const HeaderLeft = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;
const ReviewCount = styled.Text `
  font-family: ${font.pretendardMedium};
  color: ${color.gray40};
  font-size: 16px;
  line-height: 24px;
`;
const ReviewButton = styled(SccTouchableOpacity) `
  background-color: ${color.brand30};
  padding-horizontal: 14px;
  height: 31px;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  gap: 4px;
`;
const ReviewButtonText = styled.Text `
  color: ${color.white};
  font-size: 13px;
  line-height: 18px;
  font-family: ${font.pretendardBold};
`;
const SectionColumn = styled.View `
  flex-direction: column;
  gap: 4px;
  margin-top: 16px;
`;
const SectionTitle = styled.Text `
  font-family: ${font.pretendardSemibold};
  font-size: 16px;
  line-height: 26px;
  color: ${color.gray90};
`;
const MobilitySectionColumn = styled.View `
  flex-direction: column;
  gap: 12px;
  margin-top: 32px;
`;
const MobilityCardGrid = styled.View `
  flex-direction: column;
  gap: 4px;
`;
const TextBoxRow = styled.View `
  flex-direction: row;
  width: 100%;
  gap: 4px;
`;
const TextBoxThinRow = styled.View `
  flex-direction: column;
  width: 100%;
  gap: 4px;
`;
const TextBox = ({ label, content, level, shape }) => (_jsxs(TextBoxContainer, { level: level, shape: shape, children: [_jsx(View, { style: { flexGrow: 1, justifyContent: 'center' }, children: _jsx(RecommendTargetTextBoxLabel, { children: label }) }), _jsx(TextBoxContent, { level: level, shape: shape, children: content })] }));
const SpaciousTextBox = ({ label, content, level, filledRatio = 1 }) => {
    const background = level === 'high'
        ? color.brand10
        : level === 'medium'
            ? color.brand5
            : 'transparent';
    const isHighlighted = level === 'high' || level === 'medium';
    return (_jsxs(View, { style: {
            backgroundColor: 'transparent',
            borderRadius: 10,
            height: 40,
        }, children: [_jsx(View, { style: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${Math.min(Math.max(filledRatio, 0), 1) * 100}%`,
                    backgroundColor: background,
                    borderRadius: 7,
                } }), _jsxs(View, { style: {
                    paddingVertical: 10,
                    paddingHorizontal: 12,
                    flexGrow: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 4,
                }, children: [_jsx(Text, { style: {
                            fontSize: 14,
                            lineHeight: 20,
                            letterSpacing: -0.28,
                            fontFamily: isHighlighted
                                ? font.pretendardMedium
                                : font.pretendardRegular,
                            color: color.gray100,
                            backgroundColor: 'transparent',
                        }, children: label }), _jsx(Text, { style: {
                            fontSize: 13,
                            lineHeight: 18,
                            letterSpacing: -0.26,
                            fontFamily: isHighlighted
                                ? font.pretendardBold
                                : font.pretendardMedium,
                            color: isHighlighted ? color.brand50 : color.gray40,
                            textAlign: 'center',
                            backgroundColor: 'transparent',
                        }, children: content })] })] }));
};
const TextBoxContainer = styled.View `
  flex: 1;
  padding: ${({ shape }) => shape === 'thin' ? '8px 12px' : shape === 'flat' ? '12px' : '12px'};
  background-color: ${({ level }) => level === 'high'
    ? color.brand10
    : level === 'medium'
        ? color.brand5
        : color.gray10};
  border-radius: 12px;
  flex-direction: ${({ shape }) => shape === 'thin' ? 'row' : shape === 'flat' ? 'row' : 'column'};
  align-items: center;
  justify-content: ${({ shape }) => shape === 'thin' ? 'space-between' : 'center'};
  gap: 4px;
`;
const RecommendTargetTextBoxLabel = styled.Text `
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.28px;
  font-family: ${font.pretendardRegular};
  color: ${color.gray100};
  text-align: center;
`;
const TextBoxContent = styled.Text `
  font-size: 13px;
  line-height: 18px;
  letter-spacing: -0.26px;
  font-family: ${({ shape, level }) => {
    const isHighlighted = level === 'high' || level === 'medium';
    if (shape === 'thin') {
        return isHighlighted ? font.pretendardBold : font.pretendardMedium;
    }
    return isHighlighted ? font.pretendardMedium : font.pretendardRegular;
}};
  color: ${({ level }) => level === 'high'
    ? color.brand50
    : level === 'medium'
        ? color.brand50
        : color.gray40};
  text-align: center;
`;
// 이동수단 타입 한글 라벨 매핑
const MOBILITY_TYPE_LABELS = {
    MANUAL_WHEELCHAIR: '수동휠체어\n사용자',
    ELECTRIC_WHEELCHAIR: '전동휠체어\n사용자',
    STROLLER: '유아차\n동반인',
    ELDERLY: '고령자',
    NOT_SURE: '잘 모르겠음',
    NONE: '안함',
};
// 내부공간 타입 한글 라벨 매핑
const SPACIOUS_TYPE_LABELS = {
    WIDE: '🥰 매우 넓어 이용하기 아주 편리해요',
    ENOUGH: '😀 대부분 구역에서 문제없이 이용할 수 있어요',
    LIMITED: '🙂 일부 구역만 이용할 수 있어요',
    TIGHT: '🥲 매우 좁아 내부 이동이 거의 불가능해요',
};
function assignLevels(sorted) {
    if (sorted.length === 0)
        return [];
    const uniqueCounts = Array.from(new Set(sorted.map(item => item.count))).sort((a, b) => b - a);
    if (uniqueCounts.length >= 3) {
        const [high, medium] = uniqueCounts;
        return sorted.map(item => {
            if (item.count === high)
                return Object.assign(Object.assign({}, item), { level: 'high' });
            if (item.count === medium)
                return Object.assign(Object.assign({}, item), { level: 'medium' });
            return Object.assign(Object.assign({}, item), { level: 'low' });
        });
    }
    if (uniqueCounts.length === 2) {
        const [medium] = uniqueCounts;
        return sorted.map(item => item.count === medium
            ? Object.assign(Object.assign({}, item), { level: 'medium' }) : Object.assign(Object.assign({}, item), { level: 'low' }));
    }
    return sorted.map(item => (Object.assign(Object.assign({}, item), { level: 'low' })));
}
function countSpaciousType(reviews) {
    const count = {};
    Object.values(SpaciousTypeDto).forEach(type => {
        count[type] = 0;
    });
    reviews.forEach(r => {
        const type = r.spaciousType;
        if (type && count.hasOwnProperty(type)) {
            count[type]++;
        }
    });
    const SPACIOUS_ORDER = [
        SpaciousTypeDto.Wide,
        SpaciousTypeDto.Enough,
        SpaciousTypeDto.Limited,
        SpaciousTypeDto.Tight,
    ];
    const sorted = SPACIOUS_ORDER.map(type => ({
        label: SPACIOUS_TYPE_LABELS[type] || type,
        count: count[type] || 0,
    }));
    return assignLevels(sorted);
}
function countMobilityTypes(reviews) {
    const count = {};
    Object.values(RecommendedMobilityTypeDto).forEach(type => {
        count[type] = 0;
    });
    reviews.forEach(r => {
        var _a;
        (_a = r.recommendedMobilityTypes) === null || _a === void 0 ? void 0 : _a.forEach(type => {
            if (count.hasOwnProperty(type)) {
                count[type]++;
            }
        });
    });
    const MOBILITY_ORDER = [
        RecommendedMobilityTypeDto.ManualWheelchair,
        RecommendedMobilityTypeDto.ElectricWheelchair,
        RecommendedMobilityTypeDto.Stroller,
        RecommendedMobilityTypeDto.Elderly,
        RecommendedMobilityTypeDto.NotSure,
    ];
    const sorted = MOBILITY_ORDER.map(type => ({
        label: MOBILITY_TYPE_LABELS[type] || type,
        count: count[type] || 0,
    }));
    return assignLevels(sorted);
}
